/**
 * "See how" (approved mockup outputs/help-center-mockup-20261029.html, screen 3): a quiet link on
 * pages that have a how-to video. It opens the video over the page; "Read the article" goes to the
 * full Help Center article.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { dur, posterSrc, videoById, videoSrc } from "../help/content";

const Play = ({ size = 12 }: { size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true"><path d="M8 5v14l11-7z" fill="currentColor" /></svg>
);
export { Play as PlayIcon };

export function VideoModal({ id, onClose }: { id: string; onClose: () => void }) {
  const v = videoById(id);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", k);
    ref.current?.querySelector<HTMLButtonElement>("button")?.focus();
    return () => document.removeEventListener("keydown", k);
  }, []);
  if (!v) return null;
  return createPortal(
    <div className="hv-scrim" onClick={onClose}>
      <div className="hv-modal" role="dialog" aria-modal="true" aria-label={v.title} ref={ref} onClick={(e) => e.stopPropagation()}>
        <div className="hv-h"><b>{v.title}</b><button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button></div>
        <video className="hv-video" src={videoSrc(v.id)} poster={posterSrc(v.id)} controls autoPlay playsInline preload="metadata" />
        <div className="hv-f"><span className="cap">{dur(v.secs)} · narrated, with captions on screen</span>
          <Link className="hv-link" to={`/help/${v.article}`} onClick={onClose}>Read the article</Link></div>
      </div>
    </div>,
    document.body,
  );
}

/** The link. Shows nothing if the video isn't known. */
export function SeeHow({ video, label = "See how", time = true }: { video: string; label?: string; time?: boolean }) {
  const [open, setOpen] = useState(false);
  const v = videoById(video);
  if (!v) return null;
  return (
    <>
      <button type="button" className="seehow" onClick={() => setOpen(true)} aria-label={`${label}: ${v.title} (video, ${dur(v.secs)})`}>
        <Play />{label}{time && <span className="cap">{dur(v.secs)}</span>}
      </button>
      {open && <VideoModal id={v.id} onClose={() => setOpen(false)} />}
    </>
  );
}
